import React from 'react';
import { useTranslation } from 'react-i18next';

type LineTypePickerProps = {
  value?: string;
  options: string[];
  mixed?: boolean;
  mixedLabel?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
};

/**
 * Compact solid/dashed picker with a drawn stroke preview instead of text labels.
 */
const LineTypePicker: React.FC<LineTypePickerProps> = ({
  value,
  options,
  mixed = false,
  mixedLabel,
  disabled = false,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="flex w-[88px] shrink-0 gap-1"
      role="group"
      aria-label={t('propertyPanel.style')}
    >
      {options.map((option) => {
        const selected = !mixed && value === option;
        const label =
          option === 'dashed'
            ? t('propertyPanel.lineTypeDashed')
            : option === 'solid'
              ? t('propertyPanel.lineTypeSolid')
              : option;
        const title = mixed && mixedLabel ? `${label} (${mixedLabel})` : label;

        return (
          <button
            key={option}
            type="button"
            aria-label={label}
            aria-pressed={selected}
            title={title}
            disabled={disabled}
            className={`flex h-8 min-w-0 flex-1 items-center justify-center rounded-sm border px-1 ${
              selected
                ? 'border-gray-800 bg-gray-100 text-gray-900'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
            } disabled:cursor-not-allowed disabled:opacity-50`}
            onClick={() => {
              if (!disabled) {
                onChange(option);
              }
            }}
          >
            <svg
              className="h-3 w-full"
              viewBox="0 0 40 8"
              aria-hidden
              focusable="false"
            >
              <line
                x1="2"
                y1="4"
                x2="38"
                y2="4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray={option === 'dashed' ? '4 3' : undefined}
              />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

export default LineTypePicker;
